/**
 * Single source of truth for the attendance performance-rating scale.
 *
 * Business rules this encodes (and deliberately does NOT change):
 *   - `max_rating` (CompanySettings.rating_config.max_rating) is a CAP, not a
 *     scale factor. The stored rating is whatever the formula produced.
 *   - Nothing here normalizes, rescales or recomputes a rating. Every helper
 *     is presentation-only: it answers "how far along the configured scale is
 *     this value?" so the UI can render it honestly at any max.
 *   - Clamping is the one transformation applied, mirroring the server:
 *     finalRating = min(calculatedRating, maxRating).
 *
 * BANDS ARE ANCHORED TO `base_rating`, NOT TO A FIXED FRACTION OF THE MAX.
 *
 * `base_rating` is the score every employee starts at and returns to on the
 * annual reset, so it is by definition the normal, unremarkable state — "Good".
 * Everything above it is earned; everything below it is lost. The bands are
 * therefore measured from base, not from a hardcoded percentage of max.
 *
 * The previous implementation used fractions of `max_rating` with the lowest
 * cut-off at 70%. That only ever worked because the legacy default was
 * base 4 / max 5 = 80%, which happens to land in "Good". A company configured
 * base 6 / max 10 starts everyone at 60% — below the old floor — so every
 * employee read "Needs Improvement" on their first day and could never escape
 * it. Nothing in `ratingSettingsSchema` ties base to max, so any non-default
 * scale was silently broken.
 *
 * Given B = base_rating, M = max_rating and the headroom H = M - B:
 *
 *     Outstanding        rating >= B + 0.80 * H
 *     Excellent          rating >= B + 0.50 * H
 *     Good               rating >= B
 *     Satisfactory       rating >= 0.875 * B
 *     Needs Improvement  below that
 *
 * At the legacy default (B = 4, M = 5) these reproduce the previous absolute
 * cut-offs EXACTLY — 4.8 / 4.5 / 4.0 / 3.5 — so companies already on 4/5 see no
 * label change at all. At B = 6, M = 10 they become 9.2 / 8.0 / 6.0 / 5.25.
 *
 * "Satisfactory" is retained as a narrow warning zone just below base rather
 * than dropping straight to red the moment someone dips under it.
 */

export const DEFAULT_MAX_RATING = 5;

/**
 * Where base sits on the scale when it is missing or unusable. 0.8 reproduces
 * the historic default (4 of 5), so an absent `base_rating` degrades to exactly
 * the behaviour that shipped before this module knew about base at all.
 */
export const DEFAULT_BASE_FRACTION = 0.8;

/** Stars are a fixed-width gauge; the rating is mapped onto them proportionally. */
export const STAR_COUNT = 5;

/**
 * Band edges are COMPUTED floats, so an exactly-on-the-boundary rating must not
 * fall through: `6 + 0.8 * 4` is 9.200000000000001 while the literal 9.2 is
 * 9.199999999999999. Without this, an employee sitting precisely on the
 * Outstanding line would read "Excellent".
 */
const EPSILON = 1e-9;

const toNumber = (value, fallback = 0) =>
  Number.isFinite(Number(value)) ? Number(value) : fallback;

/**
 * A max can arrive as a bare number (every pre-existing call site) or inside a
 * config-ish object. Reading both keeps `clampRating` / `ratingPercent` /
 * `ratingStars` / `formatRating` working unchanged at their existing call sites
 * while letting them also accept a resolved scale.
 */
const readMax = (input) => {
  if (input === null || input === undefined) return undefined;
  if (typeof input !== "object") return input;
  return input.max ?? input.maxRating ?? input.max_rating;
};

/**
 * A base arrives as `base_rating` from `rating_config` / `GET /admin/ratings`,
 * or as `starting_rating` — the misleading name `GET /employee/rating-data`
 * gives the very same field. Both are read so no caller has to remember which
 * endpoint's payload it happens to be holding.
 */
const readBase = (input) => {
  if (!input || typeof input !== "object") return undefined;
  return (
    input.base ?? input.baseRating ?? input.base_rating ?? input.starting_rating
  );
};

/** Guards against a missing/zero/negative max, which would divide by zero. */
export const normalizeMaxRating = (maxRating) => {
  const max = Number(readMax(maxRating));
  return Number.isFinite(max) && max > 0 ? max : DEFAULT_MAX_RATING;
};

/**
 * The anchor for "Good", guarded. It must sit strictly BELOW the cap, or there
 * is no headroom left to reward and every band above Good collapses onto the
 * ceiling. Missing, non-numeric, `<= 0` and `>= max` all fall back to 80% of the
 * max, which reproduces the legacy 4-of-5 anchor exactly.
 *
 * `base === max` is reachable — Settings.jsx only rejects `base > max` — and is
 * treated as unusable rather than special-cased, so `headroom > 0` is an
 * invariant every consumer can rely on.
 */
export const normalizeBaseRating = (baseRating, maxRating) => {
  const max = normalizeMaxRating(maxRating);
  const base = Number(baseRating);
  return Number.isFinite(base) && base > 0 && base < max
    ? base
    : max * DEFAULT_BASE_FRACTION;
};

const isScale = (value) =>
  Boolean(value) && typeof value === "object" && value.__ratingScale === true;

/**
 * Normalizes anything scale-shaped into a resolved, frozen `{ base, max,
 * headroom, thresholds }`. Idempotent, so a resolved scale can be passed down
 * through props and re-resolved for free.
 *
 * Deliberately tolerant. A caller that passes only a maximum — as every call
 * site did before this change — gets the LEGACY thresholds rather than a table
 * of `undefined` comparisons that would label everyone "Needs Improvement",
 * which is the exact bug being fixed. `baseConfigured` records whether a real
 * base actually arrived, so a missed call site is detectable in QA.
 */
export const resolveScale = (input) => {
  if (isScale(input)) return input;

  const max = normalizeMaxRating(input);
  const rawBase = readBase(input);
  const base = normalizeBaseRating(rawBase, max);
  const headroom = max - base;

  return Object.freeze({
    __ratingScale: true,
    base,
    max,
    headroom,
    baseConfigured: Number(rawBase) === base,
    thresholds: Object.freeze({
      outstanding: base + headroom * 0.8,
      excellent: base + headroom * 0.5,
      good: base,
      satisfactory: base * 0.875,
      needs_improvement: 0,
    }),
  });
};

/*
 * The four helpers below take a scale OR a bare max in slot 2 — `readMax`
 * accepts either — so every pre-existing call site keeps working untouched
 * while new ones can hand over a resolved scale.
 */

/**
 * The displayable rating: the stored value, floored at 0 and capped at the
 * configured maximum. Mirrors the server-side cap so a legacy row saved before
 * the max was lowered can never render above the new ceiling.
 */
export const clampRating = (rating, scale) =>
  Math.min(Math.max(0, toNumber(rating)), normalizeMaxRating(scale));

/**
 * Progress toward the configured maximum, 0–100.
 *
 * Deliberately still measured against the MAX, not the base: the bar answers
 * "how far along the whole scale am I?", which stays true regardless of where
 * the bands sit. Only the band label and the colour are base-anchored.
 */
export const ratingPercent = (rating, scale) => {
  const max = normalizeMaxRating(scale);
  return (clampRating(rating, max) / max) * 100;
};

/** Proportional star count — `(rating / maxRating) × 5`, fractions preserved. */
export const ratingStars = (rating, scale) =>
  (ratingPercent(rating, scale) / 100) * STAR_COUNT;

/** Ordered high → low. `key` indexes `scale.thresholds`. */
export const BANDS = Object.freeze([
  { key: "outstanding", label: "Outstanding", tone: "positive" },
  { key: "excellent", label: "Excellent", tone: "positive" },
  { key: "good", label: "Good", tone: "primary" },
  { key: "satisfactory", label: "Satisfactory", tone: "caution" },
  { key: "needs_improvement", label: "Needs Improvement", tone: "critical" },
]);

/** Rank 4 (Outstanding) … 0 (Needs Improvement), for "at least this band" tests. */
const BAND_RANK = new Map(
  BANDS.map((band, index) => [band.key, BANDS.length - 1 - index]),
);

/**
 * The full band record plus the scale, clamped value and threshold that
 * produced it. Prefer this wherever a component needs more than one of
 * label/tone/threshold, so the scale is resolved once rather than three times.
 */
export const ratingBandInfo = (rating, scale) => {
  const resolved = resolveScale(scale);
  const value = clampRating(rating, resolved);
  const band =
    BANDS.find(
      (candidate) => value + EPSILON >= resolved.thresholds[candidate.key],
    ) || BANDS[BANDS.length - 1];

  return {
    ...band,
    scale: resolved,
    value,
    threshold: resolved.thresholds[band.key],
  };
};

/** Threshold label — "Outstanding", "Excellent", … */
export const ratingBand = (rating, scale) => ratingBandInfo(rating, scale).label;

/** Semantic tone key for the band, for badges and meters. */
export const ratingTone = (rating, scale) => ratingBandInfo(rating, scale).tone;

/**
 * "Is this rating at least `key`?" — replaces hardcoded percentage literals in
 * screens that gate advice on performance, so their copy can never contradict
 * the badge shown beside it.
 */
export const atLeastBand = (rating, scale, key) =>
  (BAND_RANK.get(ratingBandInfo(rating, scale).key) ?? 0) >=
  (BAND_RANK.get(key) ?? 0);

/**
 * The next band up and what it costs to reach it; `null` once Outstanding.
 *
 * `gap` is how many points short the employee is, which is the only number
 * worth putting in front of them. Replaces the arbitrary "+0.5" target step the
 * drawer used to invent, which was meaningless on a 10-point scale.
 */
export const nextBandTarget = (rating, scale) => {
  const info = ratingBandInfo(rating, scale);
  const index = BANDS.findIndex((band) => band.key === info.key);
  if (index <= 0) return null;

  const next = BANDS[index - 1];
  const threshold = info.scale.thresholds[next.key];
  return { ...next, threshold, gap: Math.max(0, threshold - info.value) };
};

/**
 * "4.020 / 10" — the canonical way to print a rating. Always shows the max, so
 * the scale is never left to the reader's assumption.
 */
export const formatRating = (rating, scale, digits = 3) =>
  `${clampRating(rating, scale).toFixed(digits)} / ${normalizeMaxRating(scale)}`;
